import { computed, nextTick, onMounted, onUnmounted, ref, watch } from "vue";
import { useHeaderMenuData } from "./useHeaderMenuData";
import { useHeaderMenuRoutes } from "./useHeaderMenuRoutes";
import { useHeaderMenuState } from "./useHeaderMenuState";
import { useHeaderScroll } from "./useHeaderScroll";
import { useHeaderSearch } from "./useHeaderSearch";
import { useMobileDrillMenu } from "./useMobileDrillMenu";

export function useHeaderPage() {
  // Shared between search/menu (guards) and scroll (owner via overlay events).
  const isExternalOverlayActive = ref(false);

  const menuData = useHeaderMenuData();
  const routes = useHeaderMenuRoutes(menuData);
  const search = useHeaderSearch({ isExternalOverlayActive });
  const menuState = useHeaderMenuState({
    isExternalOverlayActive,
    isSearchInFocus: search.isSearchInFocus,
    desktopSearchRef: search.desktopSearchRef,
    desktopMenuItems: routes.desktopMenuItems,
  });
  const drill = useMobileDrillMenu({
    routes,
    closeMobileMenu: menuState.closeMobileMenu,
    handleMobileMenuLinkNavigation: menuState.handleMobileMenuLinkNavigation,
  });

  const isInteractionActive = computed(
    () =>
      search.isSearchInFocus.value ||
      menuState.isMenuHover.value ||
      menuState.isMenuButtonsHover.value ||
      menuState.openStates.value.some((state) => state)
  );

  const scroll = useHeaderScroll({
    isExternalOverlayActive,
    isMobileMenuOpen: menuState.isMobileMenuOpen,
    isInteractionActive,
    onOverlayActivated: () => {
      menuState.openStates.value = menuState.openStates.value.map(() => false);
      search.isSearchInFocus.value = false;
      search.isSearchCantBeClosed.value = false;
      menuState.isMenuHover.value = false;
      menuState.isMenuButtonsHover.value = false;
    },
    onMobileMenuReset: () => drill.resetMobileMenu(),
  });

  // Menu-only data is requested on menu intent, not on unrelated scrolling,
  // body pointer movement or an idle callback during storefront hydration.
  let menuFiltersRequested = false;
  const prepareMenuFilters = () => {
    if (menuFiltersRequested) return;
    menuFiltersRequested = true;
    menuData.fetchMenuFilters();
  };
  watch(
    () =>
      menuState.isMenuButtonsHover.value ||
      menuState.openStates.value.some(Boolean),
    (active) => {
      if (active) prepareMenuFilters();
    },
    { flush: "sync" }
  );

  onMounted(async () => {
    scroll.beginManualVisibleHeightControl();
    // Kick off the menu/category data first so the desktop nav renders ASAP.
    // Previously these were blocked behind `await fetchCoupon()`, which delayed
    // the menu until the (unrelated) coupon request resolved.
    menuData.fetchGraphQLAPIfun();
    await menuData.fetchCoupon(scroll.updateHeaderHeight);

    document.addEventListener("click", menuState.handleClickOutside);
    document.addEventListener("keydown", menuState.handleGlobalEscape);

    await nextTick();
    scroll.updateHeaderHeight();
    scroll.measureVisibleHeaderHeight();
    scroll.attachScrollListeners();
  });

  onUnmounted(() => {
    scroll.releaseMobileMenuScroll();
    menuState.disposeMenuState();

    document.removeEventListener("click", menuState.handleClickOutside);
    document.removeEventListener("keydown", menuState.handleGlobalEscape);

    scroll.detachScrollListeners();
  });

  return {
    ...menuData,
    ...routes,
    ...search,
    ...menuState,
    ...drill,
    ...scroll,
    isExternalOverlayActive,
  };
}
